import { useState, useEffect, useRef, useCallback } from 'react';
import { apiDeleteMessage, apiGetThread, apiPostMessage, apiUpdateMessage } from '../api';

// Newest touch on a message — an edit bumps updatedAt while createdAt stays put.
const stampOf = (m) => m.updatedAt || m.createdAt;

/**
 * Live thread via smart polling — resource-friendly for a limited backend.
 *  - initial fetch, then incremental GET (?since=<lastCreatedAt>) every `intervalMs`
 *  - pauses while the tab is hidden; refetches immediately on becoming visible
 *  - dedupes by _id so optimistic sends + polls never double-count
 *
 * @param {string} token     JWT
 * @param {string} threadType 'board' | 'project' | 'todo'
 * @param {string} threadId
 * @param {object} opts       { intervalMs = 3000, enabled = true }
 */
export default function useThread(token, threadType, threadId, opts = {}) {
  const { intervalMs = 3000, enabled = true } = opts;
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const lastAtRef = useRef(null);     // ISO of the newest touch (create OR edit) we hold
  const timerRef = useRef(null);
  const mounted = useRef(true);

  const merge = useCallback((incoming) => {
    if (!incoming || incoming.length === 0) return;
    // Track the newest timestamp OUTSIDE the state updater — React StrictMode
    // double-invokes updaters in dev, so they must stay pure (no side effects).
    incoming.forEach((m) => {
      if (!lastAtRef.current || new Date(stampOf(m)) > new Date(lastAtRef.current)) {
        lastAtRef.current = stampOf(m);
      }
    });
    setMessages((prev) => {
      // Upsert by _id so an edited message REPLACES the copy we hold, while the
      // updater stays pure and idempotent (re-running it changes nothing).
      const byId = new Map(prev.map((p) => [p._id, p]));
      let changed = false;
      incoming.forEach((m) => {
        const existing = byId.get(m._id);
        if (m.hidden) {
          if (existing) {
            byId.delete(m._id);
            changed = true;
          }
          return;
        }
        if (!existing || stampOf(existing) !== stampOf(m)) {
          byId.set(m._id, m);
          changed = true;
        }
      });
      if (!changed) return prev;
      const next = [...byId.values()];
      next.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      return next;
    });
  }, []);

  const poll = useCallback(async () => {
    if (!enabled || !token || !threadId) return;
    try {
      const data = await apiGetThread(token, threadType, threadId, lastAtRef.current);
      if (mounted.current) merge(data);
    } catch (err) {
      if (mounted.current) setError(err.message);
    }
  }, [enabled, token, threadType, threadId, merge]);

  // Initial load + reset when the thread changes.
  useEffect(() => {
    mounted.current = true;
    setMessages([]);
    setLoading(true);
    setError('');
    lastAtRef.current = null;

    if (!enabled || !token || !threadId) { setLoading(false); return; }

    (async () => {
      try {
        const data = await apiGetThread(token, threadType, threadId, null);
        if (mounted.current) merge(data);
      } catch (err) {
        if (mounted.current) setError(err.message);
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();

    return () => { mounted.current = false; };
  }, [token, threadType, threadId, enabled, merge]);

  // Polling loop, paused when the tab is hidden.
  useEffect(() => {
    if (!enabled || !token || !threadId) return;

    const start = () => {
      if (timerRef.current) return;
      timerRef.current = setInterval(poll, intervalMs);
    };
    const stop = () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
    const onVisibility = () => {
      if (document.hidden) { stop(); }
      else { poll(); start(); }
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [poll, intervalMs, enabled, token, threadId]);

  const send = useCallback(async (body, metadata = {}) => {
    const text = (body || '').trim();
    if (!text) return;
    setSending(true);
    try {
      const msg = await apiPostMessage(token, {
        threadType,
        threadId,
        body: text,
        ...(metadata.milestoneId ? { milestoneId: metadata.milestoneId } : {}),
      });
      merge([msg]);
      setError('');
      return msg;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSending(false);
    }
  }, [token, threadType, threadId, merge]);

  /** Rewrite the body of a message you authored. Resolves with the saved message. */
  const edit = useCallback(async (id, body) => {
    const text = (body || '').trim();
    if (!id || !text) return null;
    try {
      const msg = await apiUpdateMessage(token, id, text);
      merge([msg]);
      setError('');
      return msg;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [token, merge]);

  /** Soft-delete a message you authored and remove it from the live thread. */
  const remove = useCallback(async (id) => {
    if (!id) return null;
    try {
      const tombstone = await apiDeleteMessage(token, id);
      merge([tombstone]);
      setError('');
      return tombstone;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [token, merge]);

  return { messages, loading, error, sending, send, edit, remove, refresh: poll };
}
